import { useEffect, useRef, useState } from "react";
import type { MotionMediaReplacementCapability } from "@blings/blings-player/editor-tools";
import type { ColorChangeHandler } from "react-color";
import { Button, Tooltip } from "antd";
import type { RcFile } from "antd/es/upload/interface";
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BorderBottomOutlined,
  BorderHorizontalOutlined,
  BorderLeftOutlined,
  BorderRightOutlined,
  BorderTopOutlined,
  BorderVerticleOutlined,
  DeleteOutlined,
  PlusOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignMiddleOutlined,
  VerticalAlignTopOutlined,
} from "@ant-design/icons";
import UploadWithTextarea from "../../Upload";
import { ColorPicker } from "../../../BaseComps/ColorPicker";
import type {
  AnchorPosition,
  AlignLayer,
  ApplyAnchorPosition,
  ApplyFieldValue,
  ApplyLayerFont,
  FieldConnectionActions,
  FieldConnectionRef,
  FieldLookup,
  FontSelectOption,
  FreezeAnimatedFields,
  LayerKind,
  OpenVisualPropertyConnector,
  ReplaceMedia,
  RemoveFieldValue,
  VisualEditableField,
  VisualPropertyTarget,
} from "./types";
import {
  BLEND_MODE_OPTIONS,
  createFallbackField,
  getFallbackFillColorProperty,
  getFallbackOutlineColorProperty,
  getFirstField,
  hasAnyField,
  toColorFieldValue,
  toColorHex,
  toNumber,
} from "./utils";
import {
  AnchorGridControl,
  CollapsibleRows,
  ColorAndOpacityControl,
  ConnectorHoverPopover,
  FieldConnectionSummary,
  FontSelectControl,
  NumericControl,
  PanelSection,
  PropertyRow,
  RotationWheelControl,
  ScaleProportionToggle,
  SegmentedButtonControl,
  SelectControl,
  SliderControl,
  TextControl,
  VectorControl,
} from "./controls";

type SectionProps = {
  fields: FieldLookup;
  onChange: ApplyFieldValue;
  onFreeze: FreezeAnimatedFields;
  connectionActions?: FieldConnectionActions;
  onOpenConnector?: OpenVisualPropertyConnector;
  layerKind?: LayerKind;
  target?: VisualPropertyTarget;
  canAddFill?: boolean;
  canAddOutline?: boolean;
};

type ColorsSectionProps = SectionProps & {
  onRemove: RemoveFieldValue;
};

type TextSectionProps = SectionProps & {
  fontOptions: FontSelectOption[];
  currentFontValue?: string;
  onFontChange: ApplyLayerFont;
  onRemove: RemoveFieldValue;
  textContentField?: VisualEditableField;
  activeTextDocumentIndex?: number;
  activeTextDocumentStyle?: Record<string, unknown>;
};

type OrientationSectionProps = SectionProps & {
  canAlign?: boolean;
  onAlign: AlignLayer;
  onAnchorPositionChange: ApplyAnchorPosition;
  activeAnchorPosition?: AnchorPosition;
};

type MediaSectionProps = {
  mediaReplacement: MotionMediaReplacementCapability;
  target?: VisualPropertyTarget;
  onReplaceMedia: ReplaceMedia;
  controls?: FieldConnectionRef[];
  connectionActions?: FieldConnectionActions;
  onOpenConnector?: OpenVisualPropertyConnector;
  uploadAssetsToProject: (
    file: RcFile,
    fileType: string,
    callback: (key: string) => void,
    pcb?: any
  ) => Promise<void>;
};

type PathSectionProps = {
  field?: VisualEditableField;
  sizeField?: VisualEditableField;
  roundCornerField?: VisualEditableField;
  onChange: ApplyFieldValue;
  onFreeze: FreezeAnimatedFields;
  connectionActions?: FieldConnectionActions;
  onOpenConnector?: OpenVisualPropertyConnector;
};

type AddColorButtonProps = {
  field?: VisualEditableField;
  label: string;
  onChange: ApplyFieldValue;
};

const TEXT_JUSTIFICATION_OPTIONS = [
  { value: 0, label: <AlignLeftOutlined />, title: "Align text left" },
  { value: 2, label: <AlignCenterOutlined />, title: "Align text center" },
  { value: 1, label: <AlignRightOutlined />, title: "Align text right" },
];

const VERTICAL_ALIGN_OPTIONS = [
  { value: 0, label: <VerticalAlignTopOutlined />, title: "Align text top" },
  {
    value: 1,
    label: <VerticalAlignMiddleOutlined />,
    title: "Align text middle",
  },
  {
    value: 2,
    label: <VerticalAlignBottomOutlined />,
    title: "Align text bottom",
  },
];

export function PathSection({
  field,
  sizeField,
  roundCornerField,
  onChange,
  onFreeze,
  connectionActions,
  onOpenConnector,
}: PathSectionProps) {
  if (!hasAnyField([field, sizeField, roundCornerField])) {
    return null;
  }

  return (
    <PanelSection title={sizeField || roundCornerField ? "Shape" : "Path"}>
      {field && (
        <PropertyRow
          label="Path"
          fields={[field]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
        />
      )}
      {sizeField && (
        <PropertyRow
          label="Size"
          fields={[sizeField]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <VectorControl
            field={sizeField}
            onChange={onChange}
            onOpenConnector={onOpenConnector}
          />
        </PropertyRow>
      )}
      {roundCornerField && (
        <PropertyRow
          label="Round corner"
          fields={[roundCornerField]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
        >
          <NumericControl
            field={roundCornerField}
            onChange={onChange}
            tooltip="Round corner"
          />
        </PropertyRow>
      )}
    </PanelSection>
  );
}

function AddColorButton({ field, label, onChange }: AddColorButtonProps) {
  if (!field) {
    return null;
  }

  return (
    <Button
      size="small"
      type="dashed"
      icon={<PlusOutlined />}
      onClick={() => onChange(field, field.value)}
    >
      Add {label}
    </Button>
  );
}

const HUE_PICKER_SELECTOR = ".hue-horizontal, .hue-vertical";

function AddColorPickerButton({
  field,
  label,
  onChange,
}: AddColorButtonProps): JSX.Element | null {
  if (!field) {
    return null;
  }

  return (
    <AddColorPickerButtonInner
      field={field}
      label={label}
      onChange={onChange}
    />
  );
}

function isHuePickerEvent(event: Parameters<ColorChangeHandler>[1]): boolean {
  const target = event?.target;
  return (
    target instanceof Element && Boolean(target.closest(HUE_PICKER_SELECTOR))
  );
}

function AddColorPickerButtonInner({
  field,
  label,
  onChange,
}: AddColorButtonProps & { field: VisualEditableField }): JSX.Element {
  const [draftColor, setDraftColor] = useState(() => toColorHex(field.value));
  const isAdjustingHueRef = useRef(false);

  useEffect(() => {
    setDraftColor(toColorHex(field.value));
  }, [field.value]);

  useEffect(() => {
    const resetHueAdjustment = () => {
      isAdjustingHueRef.current = false;
    };

    window.addEventListener("mouseup", resetHueAdjustment);
    window.addEventListener("touchend", resetHueAdjustment);

    return () => {
      window.removeEventListener("mouseup", resetHueAdjustment);
      window.removeEventListener("touchend", resetHueAdjustment);
    };
  }, []);

  const handleColorChange: ColorChangeHandler = (color, event) => {
    setDraftColor(color.hex);

    if (isHuePickerEvent(event)) {
      isAdjustingHueRef.current = true;
    }

    if (isAdjustingHueRef.current) {
      return;
    }

    onChange(field, toColorFieldValue(color.hex, field.value));
  };

  return (
    <div className="layer-visual-panel__add-color-picker">
      <ColorPicker
        color={draftColor}
        clickeable={!field.isAnimated}
        onChange={handleColorChange}
        trigger={
          <Button
            size="small"
            title={`Add ${label}`}
            aria-label={`Add ${label}`}
            icon={<PlusOutlined />}
          />
        }
      />
    </div>
  );
}

export function ColorsSection({
  fields,
  onChange,
  onRemove,
  onFreeze,
  connectionActions,
  layerKind,
  target,
  canAddFill,
  canAddOutline,
  onOpenConnector,
}: ColorsSectionProps) {
  const isTextLayer = layerKind === "text";
  const existingFillColor = isTextLayer
    ? undefined
    : getFirstField(fields, ["fillColor", "solidColor"]);
  const fallbackFillColorProperty = isTextLayer
    ? undefined
    : getFallbackFillColorProperty(layerKind, canAddFill);
  const addFillField =
    !existingFillColor && fallbackFillColorProperty
      ? createFallbackField(target, fallbackFillColorProperty)
      : undefined;
  const existingStrokeColor = isTextLayer
    ? undefined
    : getFirstField(fields, ["strokeColor"]);
  const existingStrokeWidth = isTextLayer
    ? undefined
    : getFirstField(fields, ["strokeWidth"]);
  const fallbackStrokeColorProperty = isTextLayer
    ? undefined
    : getFallbackOutlineColorProperty(layerKind, canAddOutline);
  const addStrokeField =
    !existingStrokeColor && fallbackStrokeColorProperty
      ? createFallbackField(target, fallbackStrokeColorProperty)
      : undefined;
  const fillLabel = layerKind === "solid" ? "Solid" : "Fill";
  const hasFillRow = Boolean(existingFillColor || addFillField);
  const hasOutlineRow = Boolean(existingStrokeColor || addStrokeField);
  const canRender = hasFillRow || hasOutlineRow;

  if (!canRender) {
    return null;
  }

  return (
    <PanelSection title="Colors">
      {existingFillColor ? (
        <PropertyRow
          label={fillLabel}
          fields={[existingFillColor]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <div className="layer-visual-panel__color-line">
            <ColorAndOpacityControl
              colorField={existingFillColor}
              onChange={onChange}
              colorTooltip={`${fillLabel} color`}
              onOpenConnector={onOpenConnector}
            />
            <Tooltip title={`Remove ${fillLabel.toLowerCase()}`}>
              <Button
                size="small"
                aria-label={`Remove ${fillLabel.toLowerCase()}`}
                icon={<DeleteOutlined />}
                onClick={() => onRemove(existingFillColor)}
              />
            </Tooltip>
          </div>
        </PropertyRow>
      ) : (
        addFillField && (
          <PropertyRow
            label={fillLabel}
            fields={[addFillField]}
            onFreeze={onFreeze}
            connectionActions={connectionActions}
            onOpenConnector={onOpenConnector}
          >
            <AddColorButton
              field={addFillField}
              label={fillLabel.toLowerCase()}
              onChange={onChange}
            />
          </PropertyRow>
        )
      )}
      {existingStrokeColor ? (
        <PropertyRow
          label="Outline"
          fields={[existingStrokeColor, existingStrokeWidth]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <div className="layer-visual-panel__color-line layer-visual-panel__color-line--outline">
            <ColorAndOpacityControl
              colorField={existingStrokeColor}
              onChange={onChange}
              colorTooltip="Outline color"
              onOpenConnector={onOpenConnector}
            />
            {existingStrokeWidth && (
              <NumericControl
                field={existingStrokeWidth}
                onChange={onChange}
                compact
                tooltip="Outline width"
                className="layer-visual-panel__stroke-width-field"
                onOpenConnector={onOpenConnector}
              />
            )}
            <Tooltip title="Remove outline">
              <Button
                size="small"
                aria-label="Remove outline"
                icon={<DeleteOutlined />}
                onClick={() => onRemove(existingStrokeColor)}
              />
            </Tooltip>
          </div>
        </PropertyRow>
      ) : (
        addStrokeField && (
          <PropertyRow
            label="Outline"
            fields={[addStrokeField]}
            onFreeze={onFreeze}
            connectionActions={connectionActions}
            onOpenConnector={onOpenConnector}
          >
            <AddColorButton
              field={addStrokeField}
              label="outline"
              onChange={onChange}
            />
          </PropertyRow>
        )
      )}
    </PanelSection>
  );
}

export function MediaSection({
  mediaReplacement,
  target,
  onReplaceMedia,
  controls,
  connectionActions,
  onOpenConnector,
  uploadAssetsToProject,
}: MediaSectionProps) {
  const [draftUrl, setDraftUrl] = useState(mediaReplacement.currentUrl ?? "");

  useEffect(() => {
    setDraftUrl(mediaReplacement.currentUrl ?? "");
  }, [mediaReplacement.currentUrl]);

  const handleMediaConnect = onOpenConnector
    ? () => onOpenConnector([], { preferredType: "media" })
    : undefined;

  if (!mediaReplacement.enabled) {
    return null;
  }

  if (controls?.length) {
    return (
      <PanelSection title="Media">
        <div className="layer-visual-panel__media-row">
          <div className="layer-visual-panel__row-label">Replace</div>
          <div className="layer-visual-panel__media-upload">
            <ConnectorHoverPopover onConnect={handleMediaConnect}>
              <FieldConnectionSummary
                fields={[
                  {
                    property: "refId",
                    path: "refId",
                    rootPath: "refId",
                    label: "Media",
                    group: "Media",
                    kind: "media",
                    valueType: "string",
                    value: mediaReplacement.currentUrl ?? "",
                    canAnimate: false,
                    isAnimated: false,
                    keyframeCount: 0,
                    layerName: target?.layerName ?? "",
                    layerUid: target?.layerUid,
                    assetId: target?.assetId,
                    controlled: true,
                    controls,
                    controlState:
                      controls.length > 1 ? "conflict" : "controlled",
                    writePolicy:
                      controls.length > 1 ? "choose-target" : "blocked",
                  },
                ]}
                actions={connectionActions}
              />
            </ConnectorHoverPopover>
          </div>
        </div>
      </PanelSection>
    );
  }

  const commitMediaUrl = (value: string) => {
    setDraftUrl(value);
    const trimmedValue = value.trim();

    if (trimmedValue && trimmedValue !== mediaReplacement.currentUrl) {
      onReplaceMedia({ url: trimmedValue });
    }
  };

  return (
    <PanelSection title="Media">
      <div className="layer-visual-panel__media-row">
        <div className="layer-visual-panel__row-label">Replace</div>
        <div className="layer-visual-panel__media-upload">
          <ConnectorHoverPopover onConnect={handleMediaConnect}>
            <UploadWithTextarea
              uploadAssetsToProject={uploadAssetsToProject}
              onChange={commitMediaUrl}
              value={draftUrl}
              mimeType={mediaReplacement.kind}
              placeholder="Paste media URL"
            />
          </ConnectorHoverPopover>
        </div>
      </div>
    </PanelSection>
  );
}

export function OrientationSection({
  fields,
  onChange,
  onFreeze,
  connectionActions,
  canAlign,
  onAlign,
  onAnchorPositionChange,
  activeAnchorPosition,
  onOpenConnector,
}: OrientationSectionProps) {
  const [lockScaleProportions, setLockScaleProportions] = useState(true);
  const position = fields.get("position");
  const rotation = fields.get("rotation");
  const scale = fields.get("scale");
  const skew = fields.get("skew");
  const skewAxis = fields.get("skewAxis");
  const anchor = fields.get("anchor");
  const canRender = hasAnyField([
    position,
    rotation,
    scale,
    skew,
    skewAxis,
    anchor,
  ]);

  if (!canRender) {
    return null;
  }

  return (
    <PanelSection title="Size and position">
      {canAlign && position && !position.isAnimated && (
        <div className="layer-visual-panel__align-toolbar">
          <Tooltip title="Align left">
            <Button
              size="small"
              aria-label="Align left"
              icon={<BorderLeftOutlined />}
              onClick={() => onAlign({ horizontal: "left" })}
            />
          </Tooltip>
          <Tooltip title="Align horizontal center">
            <Button
              size="small"
              aria-label="Align horizontal center"
              icon={<BorderHorizontalOutlined />}
              onClick={() => onAlign({ horizontal: "center" })}
            />
          </Tooltip>
          <Tooltip title="Align right">
            <Button
              size="small"
              aria-label="Align right"
              icon={<BorderRightOutlined />}
              onClick={() => onAlign({ horizontal: "right" })}
            />
          </Tooltip>
          <span className="layer-visual-panel__align-divider" />
          <Tooltip title="Align top">
            <Button
              size="small"
              aria-label="Align top"
              icon={<BorderTopOutlined />}
              onClick={() => onAlign({ vertical: "top" })}
            />
          </Tooltip>
          <Tooltip title="Align vertical middle">
            <Button
              size="small"
              aria-label="Align vertical middle"
              icon={<BorderVerticleOutlined />}
              onClick={() => onAlign({ vertical: "middle" })}
            />
          </Tooltip>
          <Tooltip title="Align bottom">
            <Button
              size="small"
              aria-label="Align bottom"
              icon={<BorderBottomOutlined />}
              onClick={() => onAlign({ vertical: "bottom" })}
            />
          </Tooltip>
        </div>
      )}
      {position && (
        <PropertyRow
          label="Position"
          fields={[position]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <VectorControl
            field={position}
            onChange={onChange}
            onOpenConnector={onOpenConnector}
          />
        </PropertyRow>
      )}
      {scale && (
        <PropertyRow
          label="Scale"
          fields={[scale]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <div className="layer-visual-panel__scale-inline-control">
            <ScaleProportionToggle
              checked={lockScaleProportions}
              onChange={setLockScaleProportions}
              showLabel={false}
            />
            <VectorControl
              field={scale}
              onChange={onChange}
              unit="%"
              linked={lockScaleProportions}
              onOpenConnector={onOpenConnector}
            />
          </div>
        </PropertyRow>
      )}
      {rotation && (
        <PropertyRow
          label="Rotation"
          fields={[rotation]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
        >
          <RotationWheelControl field={rotation} onChange={onChange} />
        </PropertyRow>
      )}
      {hasAnyField([skew, skewAxis]) && (
        <PropertyRow
          label="Skew"
          fields={[skew, skewAxis]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <div className="layer-visual-panel__split-controls">
            {skew && (
              <NumericControl
                field={skew}
                onChange={onChange}
                unit="deg"
                prefix="X"
                tooltip="Horizontal skew"
                onOpenConnector={onOpenConnector}
              />
            )}
            {skewAxis && (
              <NumericControl
                field={skewAxis}
                onChange={onChange}
                unit="deg"
                prefix="Y"
                tooltip="Vertical skew"
                onOpenConnector={onOpenConnector}
              />
            )}
          </div>
        </PropertyRow>
      )}
      {anchor && (
        <PropertyRow
          label="Anchor"
          fields={[anchor]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <div className="layer-visual-panel__anchor-control">
            <div className="layer-visual-panel__anchor-vector">
              <VectorControl
                field={anchor}
                onChange={onChange}
                compact
                count={2}
                onOpenConnector={onOpenConnector}
              />
            </div>
            <AnchorGridControl
              value={activeAnchorPosition}
              disabled={anchor.isAnimated}
              onChange={(position) => onAnchorPositionChange(anchor, position)}
            />
          </div>
        </PropertyRow>
      )}
    </PanelSection>
  );
}

export function TextSection({
  fields,
  onChange,
  onRemove,
  onFreeze,
  connectionActions,
  textContentField,
  layerKind,
  target,
  fontOptions,
  currentFontValue,
  onFontChange,
  activeTextDocumentIndex,
  activeTextDocumentStyle,
  onOpenConnector,
}: TextSectionProps) {
  const text = textContentField ?? fields.get("text");
  const fontFamily = fields.get("fontFamily");
  const fontSize = fields.get("fontSize");
  const lineHeight = fields.get("lineHeight");
  const characterSpacing = fields.get("tracking");
  const isTextLayer = layerKind === "text";
  const existingTextFillColor = fields.get("textFillColor");
  const textFillColor =
    existingTextFillColor ??
    (isTextLayer
      ? createFallbackField(target, "textFillColor", {
          textDocumentIndex: activeTextDocumentIndex,
          ...(activeTextDocumentStyle &&
          Object.prototype.hasOwnProperty.call(activeTextDocumentStyle, "fc")
            ? { value: activeTextDocumentStyle.fc }
            : {}),
        })
      : undefined);
  const existingTextStrokeColor = fields.get("textStrokeColor");
  const textStrokeColor =
    existingTextStrokeColor ??
    (isTextLayer
      ? createFallbackField(target, "textStrokeColor", {
          textDocumentIndex: activeTextDocumentIndex,
          ...(activeTextDocumentStyle &&
          Object.prototype.hasOwnProperty.call(activeTextDocumentStyle, "sc")
            ? { value: activeTextDocumentStyle.sc }
            : {}),
        })
      : undefined);
  const textStrokeWidth = fields.get("textStrokeWidth");
  const textJustification = fields.get("textJustification");
  const verticalAlign = fields.get("verticalAlign");
  const textBoxSize = fields.get("textBoxSize");
  const canRender = hasAnyField([
    text,
    fontFamily,
    fontSize,
    textFillColor,
    textStrokeColor,
    textStrokeWidth,
    lineHeight,
    characterSpacing,
    textJustification,
    verticalAlign,
    textBoxSize,
  ]);

  if (!canRender) {
    return null;
  }

  return (
    <PanelSection title="Text">
      {fontFamily && (
        <PropertyRow
          label="Font"
          fields={[fontFamily]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
        >
          <FontSelectControl
            field={fontFamily}
            value={currentFontValue}
            options={fontOptions}
            onChange={onFontChange}
          />
        </PropertyRow>
      )}
      {textFillColor && (
        <PropertyRow
          label="Color"
          fields={[textFillColor]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <ColorAndOpacityControl
            colorField={textFillColor}
            onChange={onChange}
            colorTooltip="Text color"
            onOpenConnector={onOpenConnector}
          />
        </PropertyRow>
      )}
      {fontSize && (
        <PropertyRow
          label="Size"
          fields={[fontSize]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
        >
          <NumericControl field={fontSize} onChange={onChange} />
        </PropertyRow>
      )}

      {hasAnyField([
        text,
        lineHeight,
        characterSpacing,
        textStrokeColor,
        textJustification,
        verticalAlign,
        textBoxSize,
      ]) && (
        <CollapsibleRows title="More text options">
          {text && (
            <PropertyRow
              label="Content"
              fields={[text]}
              onFreeze={onFreeze}
              connectionActions={connectionActions}
              onOpenConnector={onOpenConnector}
            >
              <TextControl field={text} onChange={onChange} />
            </PropertyRow>
          )}
          {hasAnyField([lineHeight, characterSpacing]) && (
            <PropertyRow
              label="Type"
              fields={[lineHeight, characterSpacing]}
              onFreeze={onFreeze}
              connectionActions={connectionActions}
              onOpenConnector={onOpenConnector}
              connectValue={false}
            >
              <div className="layer-visual-panel__text-metrics-control">
                {lineHeight && (
                  <NumericControl
                    field={lineHeight}
                    onChange={onChange}
                    prefix="LH"
                    compact
                    tooltip="Line height"
                    onOpenConnector={onOpenConnector}
                  />
                )}
                {characterSpacing && (
                  <NumericControl
                    field={characterSpacing}
                    onChange={onChange}
                    prefix="CS"
                    compact
                    tooltip="Character spacing"
                    onOpenConnector={onOpenConnector}
                  />
                )}
              </div>
            </PropertyRow>
          )}
          {existingTextStrokeColor ? (
            <PropertyRow
              label="Outline"
              fields={[existingTextStrokeColor, textStrokeWidth]}
              onFreeze={onFreeze}
              connectionActions={connectionActions}
              onOpenConnector={onOpenConnector}
              connectValue={false}
            >
              <div className="layer-visual-panel__color-line layer-visual-panel__color-line--outline">
                <ColorAndOpacityControl
                  colorField={existingTextStrokeColor}
                  onChange={onChange}
                  colorTooltip="Text outline color"
                  onOpenConnector={onOpenConnector}
                />
                {textStrokeWidth && (
                  <NumericControl
                    field={textStrokeWidth}
                    onChange={onChange}
                    compact
                    tooltip="Text outline width"
                    className="layer-visual-panel__stroke-width-field"
                    onOpenConnector={onOpenConnector}
                  />
                )}
                <Tooltip title="Remove outline">
                  <Button
                    size="small"
                    aria-label="Remove outline"
                    icon={<DeleteOutlined />}
                    onClick={() => onRemove(existingTextStrokeColor)}
                  />
                </Tooltip>
              </div>
            </PropertyRow>
          ) : (
            textStrokeColor && (
              <PropertyRow
                label="Outline"
                fields={[textStrokeColor]}
                onFreeze={onFreeze}
                connectionActions={connectionActions}
                onOpenConnector={onOpenConnector}
              >
                <AddColorPickerButton
                  field={textStrokeColor}
                  label="outline"
                  onChange={onChange}
                />
              </PropertyRow>
            )
          )}
          {textStrokeWidth && !existingTextStrokeColor && (
            <PropertyRow
              label="Weight"
              fields={[textStrokeWidth]}
              onFreeze={onFreeze}
              connectionActions={connectionActions}
              onOpenConnector={onOpenConnector}
            >
              <NumericControl
                field={textStrokeWidth}
                onChange={onChange}
                tooltip="Text outline width"
              />
            </PropertyRow>
          )}
          {hasAnyField([textJustification, verticalAlign]) && (
            <PropertyRow
              label="Align"
              fields={[textJustification, verticalAlign]}
              onFreeze={onFreeze}
              connectionActions={connectionActions}
              onOpenConnector={onOpenConnector}
              connectValue={false}
              compact
            >
              <div className="layer-visual-panel__text-align-control">
                {textJustification && (
                  <SegmentedButtonControl
                    field={textJustification}
                    value={toNumber(textJustification.value)}
                    options={TEXT_JUSTIFICATION_OPTIONS}
                    onChange={(value) => onChange(textJustification, value)}
                    onOpenConnector={onOpenConnector}
                  />
                )}
                {verticalAlign && (
                  <SegmentedButtonControl
                    field={verticalAlign}
                    value={toNumber(verticalAlign.value)}
                    options={VERTICAL_ALIGN_OPTIONS}
                    onChange={(value) => onChange(verticalAlign, value)}
                    onOpenConnector={onOpenConnector}
                  />
                )}
              </div>
            </PropertyRow>
          )}
          {textBoxSize && (
            <PropertyRow
              label="Text box"
              fields={[textBoxSize]}
              onFreeze={onFreeze}
              connectionActions={connectionActions}
              onOpenConnector={onOpenConnector}
              connectValue={false}
            >
              <VectorControl
                field={textBoxSize}
                onChange={onChange}
                compact
                onOpenConnector={onOpenConnector}
              />
            </PropertyRow>
          )}
        </CollapsibleRows>
      )}
    </PanelSection>
  );
}

export function AppearanceSection({
  fields,
  onChange,
  onFreeze,
  connectionActions,
  onOpenConnector,
}: SectionProps) {
  const opacity = fields.get("opacity");
  const hidden = fields.get("hidden");
  const blendMode = fields.get("blendMode");
  const blur = fields.get("blur");
  const canRender = hasAnyField([opacity, hidden, blendMode, blur]);

  if (!canRender) {
    return null;
  }

  return (
    <PanelSection title="Appearance">
      {opacity && (
        <PropertyRow
          label="Opacity"
          fields={[opacity]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <SliderControl
            field={opacity}
            onChange={onChange}
            unit="%"
            onOpenConnector={onOpenConnector}
          />
        </PropertyRow>
      )}
      {hidden && (
        <PropertyRow
          label="Visibility"
          fields={[hidden]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
        >
          <div className="layer-visual-panel__visibility-toggle">
            <Button
              size="small"
              disabled={hidden.isAnimated}
              type={hidden.value ? "default" : "primary"}
              onClick={() => onChange(hidden, false)}
            >
              Show
            </Button>
            <Button
              size="small"
              disabled={hidden.isAnimated}
              type={hidden.value ? "primary" : "default"}
              onClick={() => onChange(hidden, true)}
            >
              Hide
            </Button>
          </div>
        </PropertyRow>
      )}
      {hasAnyField([blendMode, blur]) && (
        <PropertyRow
          label="Effects"
          fields={[blendMode, blur]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <div className="layer-visual-panel__blend-blur-control">
            {blendMode && (
              <SelectControl
                field={blendMode}
                value={toNumber(blendMode.value)}
                options={BLEND_MODE_OPTIONS}
                onChange={(value) => onChange(blendMode, value)}
                onOpenConnector={onOpenConnector}
              />
            )}
            {blur && (
              <NumericControl
                field={blur}
                onChange={onChange}
                compact
                tooltip="Blur amount"
                onOpenConnector={onOpenConnector}
              />
            )}
          </div>
        </PropertyRow>
      )}
    </PanelSection>
  );
}

export function RevealPathSection({
  fields,
  onChange,
  onFreeze,
  connectionActions,
  onOpenConnector,
}: SectionProps) {
  const trimStart = fields.get("trimStart");
  const trimEnd = fields.get("trimEnd");
  const trimOffset = fields.get("trimOffset");
  const canRender = hasAnyField([trimStart, trimEnd, trimOffset]);

  if (!canRender) {
    return null;
  }

  return (
    <PanelSection title="Reveal path">
      {hasAnyField([trimStart, trimEnd]) && (
        <PropertyRow
          label="Length"
          fields={[trimStart, trimEnd]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
          connectValue={false}
        >
          <div className="layer-visual-panel__split-controls">
            {trimStart && (
              <NumericControl
                field={trimStart}
                onChange={onChange}
                unit="%"
                onOpenConnector={onOpenConnector}
              />
            )}
            {trimEnd && (
              <NumericControl
                field={trimEnd}
                onChange={onChange}
                unit="%"
                onOpenConnector={onOpenConnector}
              />
            )}
          </div>
        </PropertyRow>
      )}
      {trimOffset && (
        <PropertyRow
          label="Offset"
          fields={[trimOffset]}
          onFreeze={onFreeze}
          connectionActions={connectionActions}
          onOpenConnector={onOpenConnector}
        >
          <NumericControl field={trimOffset} onChange={onChange} unit="%" />
        </PropertyRow>
      )}
    </PanelSection>
  );
}
